import { ffhClient } from './ffh-client';
import type { DLConsent, PlanTier } from '@lib/analytics/dataLayer';

/**
 * FFH-990 — shared browser → server CAPI shim. POSTs a browser-originated
 * event to the backend `POST /events/track`, which runs the FFH-602 fanout to
 * Meta / Google Ads / TikTok. Built once here; reused by FFH-606 (View Content)
 * and FFH-991 (Add to Cart) by swapping `eventName` + `custom`.
 *
 * Dedup contract: the caller mints ONE Pattern A `eventId` (architecture §2.1)
 * and passes it to BOTH the dataLayer push (read by the GTM pixel) and this
 * shim, so the pixel and CAPI events join on a single id.
 *
 * Click identifiers are NOT sent in the body — they ride the HttpOnly
 * first-party cookies the backend reads server-side, which is why the request
 * MUST be credentialed (`credentials: 'include'`), exactly like the checkout
 * and funnel-email-capture calls.
 *
 * Fire-and-forget: a failed CAPI post must never break the user-facing reveal,
 * so errors (network, missing env, non-2xx) are swallowed.
 */

export type CapiEventName =
  | 'view_content'
  | 'lead'
  | 'add_to_cart'
  | 'purchase';

export interface CapiSendInput {
  eventName: CapiEventName;
  /** Pattern A event id, shared verbatim with the dataLayer push. */
  eventId: string;
  sourceUrl?: string;
  custom?: {
    value?: number;
    currency?: string;
    planTier?: PlanTier;
    subscriptionId?: string;
  };
  /** Web 4-axis consent; projected to the backend's 3-axis shape below. */
  consent?: DLConsent;
}

/**
 * The backend consent block is 3-axis (`adStorage` / `adUserData` /
 * `adPersonalization`); the web `DLConsent` carries a 4th `analytics_storage`
 * that the CAPI rail does not use. Omitting consent entirely is safe — the
 * handler defaults to denied.
 */
const toBackendConsent = (
  consent: DLConsent | undefined
):
  | {
      adStorage: 'granted' | 'denied';
      adUserData: 'granted' | 'denied';
      adPersonalization: 'granted' | 'denied';
    }
  | undefined =>
  consent
    ? {
        adStorage: consent.ad_storage,
        adUserData: consent.ad_user_data,
        adPersonalization: consent.ad_personalization
      }
    : undefined;

export const sendCapiEvent = async (input: CapiSendInput): Promise<void> => {
  const consent = toBackendConsent(input.consent);
  try {
    await ffhClient().POST('/events/track', {
      body: {
        event: input.eventName,
        eventId: input.eventId,
        ...(input.sourceUrl ? { sourceUrl: input.sourceUrl } : {}),
        ...(input.custom ? { custom: input.custom } : {}),
        ...(consent ? { consent } : {})
      },
      credentials: 'include'
    });
  } catch {
    // Swallow — the CAPI shim must never break the user-facing reveal.
  }
};
